import React, { useContext, useEffect } from "react";
import Select, { SelectOption } from "@amzn/meridian/select";
import Input from "@amzn/meridian/input";
import Tag from "@amzn/meridian/tag";
import Link from "@amzn/meridian/link";
import Alert from "@amzn/meridian/alert";
import Button from "@amzn/meridian/button";
import { FormattedMessage, injectIntl } from "react-intl";
import { InboundFilterContext } from "./context/InboundFilterContext";
import { useDeviceLayout } from "../common/context/DeviceLayoutContext";
import FilterExpander from "../common/FilterExpander";
import { fetchStationTimezone } from "./network/InboundNetworkAPI";
import './Inbound.css';

const InboundFilter = ({ intl }) => {
    const { filter, dispatch } = useContext(InboundFilterContext);
    const { isMobile } = useDeviceLayout();

    useEffect(() => {
        dispatch({ type: 'LOAD_FILTER_DATA' });
        fetchStationTimezone(dispatch);
    }, []);

    const isFilterSet = () => {
        return filter.selectedTimeRange !== '' || filter.useCustomTimeRange;
    };

    const clearAllFilters = () => {
        dispatch({ type: 'CLEAR_ALL_FILTERS' });
    };

    const removeTimeRange = () => {
        dispatch({ type: 'CLEAR_TIME_RANGE' });
    };

    const getTimeRangeDisplayName = (timeRangeId) => {
        const timeRange = filter.allTimeRanges.find(t => t.id === timeRangeId);
        return timeRange ? timeRange.label : timeRangeId;
    };

    const getCustomTimeRangeDisplay = () => {
        if (!filter.useCustomTimeRange) return '';
        const start = filter.customStartDate && filter.customStartTime
            ? `${filter.customStartDate} ${filter.customStartTime}`
            : filter.customStartDate || '';
        const end = filter.customEndDate && filter.customEndTime
            ? `${filter.customEndDate} ${filter.customEndTime}`
            : filter.customEndDate || '';
        return start && end ? `${start} - ${end}` : start || end;
    };

    const getCurrentDateTime = () => {
        const now = new Date();
        // Use station timezone if available so the max constraint on inputs matches
        // the same timezone used for validation and epoch conversion.
        const tz = filter.stationTimezone;
        if (tz) {
            try {
                const formatter = new Intl.DateTimeFormat('en-US', {
                    timeZone: tz,
                    year: 'numeric', month: '2-digit', day: '2-digit',
                    hour: '2-digit', minute: '2-digit', hour12: false
                });
                const p = Object.fromEntries(
                    formatter.formatToParts(now).map(({ type, value }) => [type, value])
                );
                const time = p.hour === '24' ? `00:${p.minute}` : `${p.hour}:${p.minute}`;
                return { date: `${p.year}-${p.month}-${p.day}`, time };
            } catch (e) {
                // fall through to browser time
            }
        }
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        return {
            date: `${year}-${month}-${day}`,
            time: `${hours}:${minutes}`
        };
    };

    const renderFilterControls = () => {
        const currentDateTime = getCurrentDateTime();
        const isCustomMode = filter.timeRangeMode === 'custom';

        return (
            <React.Fragment>
                <div className="filter-group" style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <span className="filter-label">
                        <FormattedMessage id="inbound.filter.time_range_label" defaultMessage="Time Range:" />
                    </span>
                    <div className="time-range-toggle">
                        <label className={`toggle-option${!isCustomMode ? ' active' : ''}`}>
                            <input
                                type="radio"
                                name="timeRangeMode"
                                value="relative"
                                checked={!isCustomMode}
                                onChange={function() { dispatch({ type: 'SET_TIME_RANGE_MODE', mode: 'relative' }); }}
                            />
                            <FormattedMessage id="inbound.filter.relative" defaultMessage="Relative" />
                        </label>
                        <label className={`toggle-option${isCustomMode ? ' active' : ''}`}>
                            <input
                                type="radio"
                                name="timeRangeMode"
                                value="custom"
                                checked={isCustomMode}
                                onChange={function() { dispatch({ type: 'SET_TIME_RANGE_MODE', mode: 'custom' }); }}
                            />
                            <FormattedMessage id="inbound.filter.custom" defaultMessage="Custom" />
                        </label>
                    </div>
                </div>

                {!isCustomMode && (
                    <div className="filter-group">
                        <Select
                            onChange={function(data) { dispatch({ type: 'SELECT_TIME_RANGE', timeRange: data }); }}
                            value={filter.selectedTimeRange}
                            size="small"
                            placeholder={intl.formatMessage({ id: 'inbound.filter.select_time_range', defaultMessage: 'Select Time Range' })}
                            width={200}>
                            {filter.allTimeRanges.map(function(timeRange) {
                                return (
                                    <SelectOption key={timeRange.id} label={timeRange.label} value={timeRange.id} />
                                );
                            })}
                        </Select>
                    </div>
                )}

                {isCustomMode && (
                    <div className="filter-group">
                        <div className="custom-time-range-inputs">
                            <Input
                                type="date"
                                value={filter.customStartDate}
                                max={currentDateTime.date}
                                onChange={function(value) { dispatch({ type: 'SET_CUSTOM_START_DATE', date: value }); }}
                                size="small"
                                placeholder="Start Date"
                                width={150}
                            />
                            <Input
                                type="time"
                                value={filter.customStartTime}
                                max={filter.customStartDate === currentDateTime.date ? currentDateTime.time : undefined}
                                onChange={function(value) { dispatch({ type: 'SET_CUSTOM_START_TIME', time: value }); }}
                                size="small"
                                placeholder="Start Time"
                                width={120}
                            />
                            <span className="time-range-separator">to</span>
                            <Input
                                type="date"
                                value={filter.customEndDate}
                                onChange={function(value) { dispatch({ type: 'SET_CUSTOM_END_DATE', date: value }); }}
                                size="small"
                                placeholder="End Date"
                                width={150}
                            />
                            <Input
                                type="time"
                                value={filter.customEndTime}
                                onChange={function(value) { dispatch({ type: 'SET_CUSTOM_END_TIME', time: value }); }}
                                size="small"
                                placeholder="End Time"
                                width={120}
                            />
                            <Button
                                type="primary"
                                size="small"
                                disabled={
                                    !filter.customStartDate ||
                                    !filter.customStartTime ||
                                    !filter.customEndDate ||
                                    !filter.customEndTime
                                }
                                onClick={function() { dispatch({ type: 'APPLY_CUSTOM_TIME_RANGE' }); }}
                            >
                                <FormattedMessage id="inbound.filter.apply" defaultMessage="Apply" />
                            </Button>
                        </div>
                    </div>
                )}

                {isFilterSet() && (
                    <div className="filter-tags">
                        {filter.selectedTimeRange && !isCustomMode && (
                            <Tag type="theme" onClose={removeTimeRange}>
                                {getTimeRangeDisplayName(filter.selectedTimeRange)}
                            </Tag>
                        )}
                        {filter.useCustomTimeRange && getCustomTimeRangeDisplay() && (
                            <Tag type="theme" onClose={removeTimeRange}>
                                {getCustomTimeRangeDisplay()}
                            </Tag>
                        )}
                        <Link onClick={clearAllFilters} type="secondary">
                            <FormattedMessage id="inbound.filter.clear_all" defaultMessage="Clear all" />
                        </Link>
                        {filter.customTimeRangeError && (
                            <div className="custom-time-range-error">
                                <Alert type="error">
                                    {filter.customTimeRangeError}
                                </Alert>
                            </div>
                        )}
                    </div>
                )}
                {!isFilterSet() && filter.customTimeRangeError && (
                    <div className="custom-time-range-error">
                        <Alert type="error">
                            {filter.customTimeRangeError}
                        </Alert>
                    </div>
                )}
            </React.Fragment>
        );
    };

    if (isMobile) {
        return (
            <FilterExpander
                appliedFilters={isFilterSet() ? (
                    <div className="filter-tags filter-tags-mobile">
                        {filter.selectedTimeRange && (
                            <Tag type="theme" onClose={removeTimeRange}>
                                {getTimeRangeDisplayName(filter.selectedTimeRange)}
                            </Tag>
                        )}
                        {filter.useCustomTimeRange && getCustomTimeRangeDisplay() && (
                            <Tag type="theme" onClose={removeTimeRange}>
                                {getCustomTimeRangeDisplay()}
                            </Tag>
                        )}
                        <Link onClick={clearAllFilters} type="secondary">
                            <FormattedMessage id="inbound.filter.clear_all" defaultMessage="Clear all" />
                        </Link>
                    </div>
                ) : null}>
                <div className="filter-bar">
                    {renderFilterControls()}
                </div>
            </FilterExpander>
        );
    }

    return (
        <div className="filter-bar">
            {renderFilterControls()}
        </div>
    );
};

export { InboundFilter as InboundFilterComponent };
export default injectIntl(InboundFilter);



// WEBPACK FOOTER //
// ./src/components/inbound/InboundFilter.js